import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { PostLoginAuthLink } from "@/components/routing/PostLoginAuthLink";
import { ArrowLeft, Shield, LineChart, Users, Wallet } from "lucide-react";
import { RootLayout } from "@/components/layout/RootLayout";
import { ABOUT_PAGE_SEO_DESCRIPTION, ABOUT_PAGE_SEO_TITLE } from "@/lib/landingMarketing";

/**
 * About.
 */
export default function About() {
  return (
    <RootLayout>
      <Helmet>
        <title>{ABOUT_PAGE_SEO_TITLE}</title>
        <meta name="description" content={ABOUT_PAGE_SEO_DESCRIPTION} />
        <meta
          name="keywords"
          content="financial management platform, personal finance software, investment tracking, expense management, financial planning tools, wealth management solution"
        />
        <meta property="og:title" content={ABOUT_PAGE_SEO_TITLE} />
        <meta property="og:description" content={ABOUT_PAGE_SEO_DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <main className="container mx-auto px-4 pt-32 pb-12">
          <Button variant="outline" asChild className="mb-8">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
            </Link>
          </Button>

          <article className="prose prose-lg max-w-none">
            <h1 className="text-4xl md:text-5xl font-bold mb-8">
              Transform Your Financial Management with TellerWand
            </h1>

            <section className="my-12 bg-white p-8 rounded-xl shadow-sm border border-gray-100 not-prose">
              <h2 className="text-3xl font-bold mb-4 text-gray-900">Who We Are</h2>
              <p className="text-lg text-gray-700 mb-4">
                <strong>TellerWand Inc.</strong> builds personal finance software for Indian
                investors, families, and organizations. We started with a simple idea: one place to
                see PF, PPF, mutual funds, gold, real estate, and the rest of your wealth — without
                juggling spreadsheets or multiple apps.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Our team brings together product, engineering, and finance experience. We ship
                features used every day on tellerwand.com — portfolio tracking, family finance,
                SplitMage, calculators, and educational content on our blog.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-gray-700">
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Company</p>
                  <p>TellerWand Inc.</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Contact</p>
                  <p>
                    <a
                      href="mailto:support@tellerwand.com"
                      className="text-primary hover:underline"
                    >
                      support@tellerwand.com
                    </a>
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Website</p>
                  <p>
                    <a href="https://tellerwand.com" className="text-primary hover:underline">
                      tellerwand.com
                    </a>
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Focus</p>
                  <p>Personal &amp; family wealth management for India</p>
                </div>
              </div>
            </section>

            <div className="grid md:grid-cols-2 gap-8 my-12">
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <LineChart className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-semibold mb-4">Comprehensive Portfolio Tracking</h2>
                <p>
                  Monitor all your investments in real-time with advanced analytics and performance
                  metrics. Get detailed insights into your portfolio's performance and make
                  data-driven investment decisions.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm">
                <Wallet className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-semibold mb-4">Smart Expense Management</h2>
                <p>
                  Take control of your spending with automated categorization, budgeting tools, and
                  predictive analytics that help you optimize your finances and save more.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm">
                <Shield className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-semibold mb-4">Enterprise-Level Security</h2>
                <p>
                  Your financial data is protected with enterprise-grade encryption and security
                  protocols. We implement the highest standards of data protection and privacy
                  measures.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-sm">
                <Users className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-2xl font-semibold mb-4">Personalized Financial Planning</h2>
                <p>
                  Get tailored recommendations and insights based on your financial goals and
                  spending patterns. Our AI-powered platform adapts to your unique financial
                  situation.
                </p>
              </div>
            </div>

            <section className="my-12">
              <h2 className="text-3xl font-bold mb-6">Why Choose TellerWand?</h2>
              <ul className="space-y-4">
                <li>✓ All-in-one platform for complete financial management</li>
                <li>✓ Real-time synchronization across all your accounts</li>
                <li>✓ Advanced analytics and reporting capabilities</li>
                <li>✓ Intelligent goal tracking and recommendations</li>
                <li>✓ Secure, encrypted data protection</li>
                <li>✓ Mobile-friendly access to your finances</li>
              </ul>
            </section>

            <section className="my-12">
              <h2 className="text-3xl font-bold mb-6">Our Mission and Vision</h2>
              <p className="text-lg mb-6">
                At TellerWand, we believe that everyone deserves access to powerful financial
                management tools. Our mission is to democratize financial planning and make
                sophisticated investment tracking accessible to individuals and families worldwide.
              </p>
              <p className="text-lg mb-6">
                We envision a world where financial literacy is universal, and everyone can make
                informed decisions about their money. Through our innovative platform, we're
                building the future of personal finance management.
              </p>
            </section>

            <section className="my-12">
              <h2 className="text-3xl font-bold mb-6">Advanced Features for Modern Finance</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3">Real-Time Portfolio Analytics</h3>
                  <p>
                    Get instant insights into your investment performance with our advanced
                    analytics engine. Track your portfolio's growth, analyze risk metrics, and
                    receive personalized recommendations based on market trends and your financial
                    goals.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">Intelligent Expense Categorization</h3>
                  <p>
                    Our AI-powered system automatically categorizes your expenses and identifies
                    spending patterns. Gain valuable insights into your spending habits and discover
                    opportunities to save money and optimize your budget.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">Goal-Based Financial Planning</h3>
                  <p>
                    Set and track multiple financial goals simultaneously. Whether you're saving for
                    retirement, planning a vacation, or building an emergency fund, our platform
                    helps you stay on track with personalized milestones and progress tracking.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3">Comprehensive Reporting</h3>
                  <p>
                    Generate detailed financial reports and visualizations that help you understand
                    your financial health. Export data, create custom reports, and share insights
                    with your financial advisor or family members.
                  </p>
                </div>
              </div>
            </section>
          </article>

          {/* CTA Section - Outside article to avoid prose link styling */}
          <section className="bg-primary/5 p-8 rounded-xl my-12">
            <h2 className="text-3xl font-bold mb-6">Getting Started is Easy</h2>
            <p className="mb-8 text-lg">
              Create a free account to explore portfolio tracking, family finance, and calculators
              built for Indian investors. Our platform is designed to make financial management
              accessible — whether you are just starting out or already managing a diverse
              portfolio.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" asChild>
                <PostLoginAuthLink mode="signup">
                  Start Your Financial Journey Today
                </PostLoginAuthLink>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/contact-us">Contact Our Team</Link>
              </Button>
            </div>
          </section>
        </main>
      </div>
    </RootLayout>
  );
}
